/**
 * The canonical record's process rail (#2267 port 7).
 *
 * Mounted for EVERY entity, and it renders for the ones that declare a
 * `core.status_machine`. That is the whole generalisation: the legacy CRM page
 * drew a rail from a field called `stage` and therefore had one on two entities
 * and none on the forty-odd that actually declare a lifecycle.
 *
 * ── the rail READS; it does not advance ─────────────────────────────────────
 * `ProcessBar` takes an `advance` prop precisely so a host can withhold it, and
 * this host withholds it deliberately.
 *
 * Moving a record through its lifecycle is a governed act. `core.assert_transition`
 * decides which edges are legal, an edge may be `reason_required`, and since
 * each module's own door (and, on a `direct` entity, its BEFORE trigger) carries
 * the authority, there is no generic "advance" verb to call. The legacy rail's
 * `onMove` wrote `saveEntity({ stage })` — the console writing past the doors
 * (#1131), with no reason, no legality check the server had not already been
 * forced to repeat, and no answer for an edge that needs one.
 *
 * What a person may DO to this record already has a surface: `RecordActions`
 * renders `core.my_entity_actions` in the header above this bar (#1131), and an
 * entity with a richer ceremony keeps its own mount in `RECORD_PROCESS`. So the
 * rail states position and the server states permission — which is the split
 * every other governed surface in this console already draws.
 *
 * Adding a generic advance means adding a generic governed verb first. That is
 * a platform decision and belongs in Forge, not in this file.
 */
import { ProcessBar } from '@solum/ui'
import type { Row } from '@solum/sdk'
import { useEntityStatusMachine } from '../data/queries'
import { recordProcessLabels, recordProcessRail } from './recordProcessRail'

export function RecordProcess({ entity, id, row }: { entity: string; id: string; row: Row | undefined }) {
  const machine = useEntityStatusMachine(id === 'new' ? undefined : entity)

  /* A machine read that is loading, refused or absent all answer the same way:
     no rail. `useEntityStatusMachine` already swallows a deployment that
     predates `core.status_machine`, so a missing lifecycle and an unreachable
     registry are indistinguishable here BY DESIGN — neither is evidence that
     this record has a process, and drawing one on a guess is the defect being
     removed. */
  const rail = recordProcessRail(machine.data, row as Record<string, unknown> | undefined)
  if (!machine.data || !rail) return null

  const { label, positionText } = recordProcessLabels(machine.data, rail)
  return (
    <ProcessBar
      testId="record-process"
      stages={rail.stages}
      label={label}
      positionText={positionText}
      positionTestId="record-process-position"
      /* No `advance`. See the header — the verb is not this component's to
         invent, and a disabled button would still announce one that exists. */
    />
  )
}
